import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Navbar from '@/components/Navbar';
import KPIBox from '@/components/KPIBox';
import PyramidChart from '@/components/PyramidChart';
import CreditMeterCard from '@/components/dashboard/CreditMeterCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import BrandLogo from '@/components/BrandLogo';
import {
  MessageSquare,
  Image,
  Database,
  Users,
  Eye,
  UserCheck,
  TrendingUp,
  Download,
  ArrowUpRight,
  Loader2,
  MoreHorizontal,
  Play,
  ExternalLink,
  Pause,
  Trash2,
  FileText,
  Copy,
  RefreshCw,
  Send,
  CheckCircle,
  XCircle,
  BarChart3,
  Zap,
  Flame,
  Reply,
  Coins,
  UserMinus,
  AlertCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { toast } from 'sonner';
import { formatTemplateName } from '@/lib/templateName';
import { apiFetch, authFetch } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { useWorkspace, usePermissions, useEntitlements } from '@/contexts/WorkspaceContext';
import { isLaunched } from '@/lib/campaignStatus';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { formatCredits } from '@/lib/creditCosts';

// Every figure on this page is counted by the backend from what actually happened — and refetched
// while the page is open, so a balance moves while you watch.
//
// This replaced a page where the credit card showed the total balance over the plan-only allowance
// ("124 / 50 · 0% used"), the KPI tiles and the campaign table read impressions/leads/spend that
// nothing ever wrote, every tile wore a constant "↑ 0% vs last month", the funnel's middle stages
// were the top one times 0.75 and 0.5, both credit buttons opened a NEW subscription checkout, and
// nothing refreshed after the first load.

interface CampaignStats {
  sent: number;
  delivered: number;
  failed: number;
  replies: number;
  credits: number;
  deliveryRate: number | null;
}

interface Campaign {
  id: string;
  name: string;
  status: string;
  type: string;
  createdAt: string;
  stats?: CampaignStats;
}

type SpendingCategory = 'campaign_send' | 'ai_generation' | 'test_send';

interface CreditActivity {
  id: string;
  kind: string;
  category: string;
  amount: number;
  topUpAmount: number;
  balanceAfter: number | null;
  createdAt: string;
  reference: { type: string; id: string; name: string | null } | null;
  backfilled: boolean;
}

interface WorkspaceOverview {
  workspace: { id: string; name: string; planKey?: string | null; planName?: string | null; planRenewsAt?: string | null };
  credits: {
    total: number;
    plan: {
      allowance: number;
      remaining: number;
      used: number;
      usedPercent: number | null;
      carriedOver: number;
      periodStart: string | null;
      renewsAt: string | null;
    };
    topUp: { remaining: number; purchased: number; used: number; usedPercent: number | null; cap: number | null };
    consumed: { since: string | null; total: number; byCategory: Record<SpendingCategory, number> };
    recent: CreditActivity[];
  };
  campaigns: { total: number; byStatus: Record<string, number>; byType: Record<string, number> };
  contacts: { total: number; unsubscribed: number };
  team: { members: number };
}

interface Trend {
  value: number | null;
  previous: number | null;
  change: number | null;
  unit: 'percent' | 'points';
  betterWhen: 'higher' | 'lower';
}

type KpiKey =
  | 'messagesSent' | 'deliveryRate' | 'replies' | 'contactsReached'
  | 'interestedLeads' | 'mediaCreated' | 'creditsPerReply' | 'optOuts';

interface WorkspaceInsights {
  windowDays: number;
  kpis: Record<KpiKey, Trend>;
  funnel: { stage: string; count: number }[];
}

// `read` is a stronger status than `delivered`, not an alternative to it — a read message was
// necessarily delivered. Anywhere "delivered" is displayed, read counts must be added in, or
// messages disappear from the total once they're read.
interface MessagingStats {
  total: number; pending: number; sent: number; delivered: number; read: number; failed: number;
  deliveryRate: number;
  readRate: number;
  byChannel: Record<string, { total: number; sent: number; delivered: number; read: number; failed: number }>;
  byDay: Array<{ date: string; sent: number; delivered: number; read: number; failed: number }>;
  templatePerformance: Array<{ name: string; sent: number; delivered: number; read: number; failed: number; deliveryRate: number; readRate: number }>;
}

// How often each part of the page re-asks. The credit cards are cheap and the thing people watch;
// the 30-day analytics barely move between visits.
const REFRESH_MS = {
  overview: 30 * 1000,
  campaigns: 60 * 1000,
  messaging: 60 * 1000,
  insights: 5 * 60 * 1000,
};

const SPENDING_CATEGORIES: SpendingCategory[] = ['campaign_send', 'ai_generation', 'test_send'];

const STATUS_FILTERS = ['live', 'running', 'scheduled', 'draft', 'paused', 'under_approval', 'ended'];

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspaceId, refreshWorkspace } = useWorkspace();
  const { can } = usePermissions();
  const { hasFeature } = useEntitlements();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ campaignId: string; campaignName: string; action: 'paused' | 'ended' } | null>(null);
  const [pollingDelivery, setPollingDelivery] = useState(false);

  const dashboardKey = queryKeys.dashboard(workspaceId);
  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const overviewQuery = useQuery({
    queryKey: [...dashboardKey, 'overview'],
    queryFn: () => apiFetch<WorkspaceOverview>(`/workspaces/${workspaceId}/overview`),
    refetchInterval: REFRESH_MS.overview,
    refetchOnWindowFocus: true,
  });
  const campaignsQuery = useQuery({
    queryKey: [...dashboardKey, 'campaigns'],
    queryFn: () => apiFetch<{ campaigns: Campaign[] }>(`/workspaces/${workspaceId}/campaigns`),
    refetchInterval: REFRESH_MS.campaigns,
    refetchOnWindowFocus: true,
  });
  const insightsQuery = useQuery({
    queryKey: [...dashboardKey, 'insights'],
    queryFn: () => apiFetch<WorkspaceInsights>(`/workspaces/${workspaceId}/insights`),
    refetchInterval: REFRESH_MS.insights,
    refetchOnWindowFocus: true,
    enabled: can('analytics:read'),
  });
  // Days are grouped in the viewer's timezone, not the database's UTC.
  const messagingQuery = useQuery({
    queryKey: [...dashboardKey, 'messaging', timeZone],
    queryFn: () => apiFetch<MessagingStats>(
      `/workspaces/${workspaceId}/messaging-analytics/stats?tz=${encodeURIComponent(timeZone)}`,
    ),
    refetchInterval: REFRESH_MS.messaging,
    refetchOnWindowFocus: true,
    // A plan-gated endpoint: asking without the feature only earns a 403.
    enabled: hasFeature('analytics_plus'),
    retry: false,
  });

  const overview = overviewQuery.data;
  const insights = insightsQuery.data;
  const campaigns = useMemo(() => campaignsQuery.data?.campaigns ?? [], [campaignsQuery.data]);
  const messagingStats = messagingQuery.data;
  const loading = overviewQuery.isPending || campaignsQuery.isPending;

  const formatDay = (value?: string | null) => (value
    ? new Date(value).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })
    : null);

  // Asks the provider about SMS messages we have no verdict on yet, then refreshes the
  // charts. WhatsApp statuses arrive by webhook only (the provider offers no pull
  // endpoint), so for those this is a recompute. Nothing here invents a status the way
  // the old 24-hour heuristic did.
  //
  // Shared by the button and the on-mount pass; `silent` suppresses toasts for the latter.
  const reconcileDelivery = async (activeCampaigns: Campaign[], silent = false) => {
    let totalUpdated = 0;
    for (const campaign of activeCampaigns) {
      try {
        const result = await apiFetch<{ updated?: number }>(
          `/workspaces/${workspaceId}/campaigns/${campaign.id}/poll-delivery`,
          { method: 'POST' },
        );
        totalUpdated += result.updated ?? 0;
      } catch {
        // Continue with next campaign
      }
    }

    await queryClient.invalidateQueries({ queryKey: dashboardKey });

    if (!silent) {
      if (totalUpdated > 0) toast.success(`Updated ${totalUpdated} delivery status(es)`);
      else toast.info('No new delivery updates');
    }
    return totalUpdated;
  };

  const activeCampaignsForDelivery = () =>
    campaigns.filter(c => ['live', 'running', 'ended'].includes(c.status));

  const handlePollDelivery = async () => {
    setPollingDelivery(true);
    try {
      await reconcileDelivery(activeCampaignsForDelivery());
    } catch {
      toast.error('Failed to refresh delivery statistics');
    } finally {
      setPollingDelivery(false);
    }
  };

  // Reconcile delivery once per visit, after the page has rendered.
  //
  // The provider keeps SMS status for only 3 days, and we deliberately run no scheduled
  // job, so a page visit is one of only two chances to collect it (the other being the
  // real-time receipts pushed to our webhook). Deliberately fire-and-forget: it must
  // never delay first paint. The backend debounces per message, so repeated visits don't
  // re-hit the provider.
  //
  // Gated on the permission the endpoint itself requires — without this, a viewer or
  // finance member would fire a 403 per campaign on every dashboard load.
  const deliverySyncedRef = useRef(false);
  useEffect(() => {
    if (loading || deliverySyncedRef.current) return;
    if (!can('campaign:launch')) return;

    const active = activeCampaignsForDelivery();
    if (active.length === 0) return;

    deliverySyncedRef.current = true;
    void reconcileDelivery(active, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, campaigns]);

  const kpiTiles = useMemo(() => {
    if (!insights) return [];
    const count = (v: number | null) => (v == null ? '—' : v.toLocaleString());
    const tiles: { key: KpiKey; label: string; icon: React.ReactNode; format: (v: number | null) => string }[] = [
      { key: 'messagesSent', label: t('dashboard.metrics.messages_sent'), icon: <Send className="h-6 w-6 text-primary-foreground" />, format: count },
      { key: 'deliveryRate', label: t('dashboard.metrics.delivery_rate'), icon: <CheckCircle className="h-6 w-6 text-primary-foreground" />, format: (v) => (v == null ? '—' : `${v}%`) },
      { key: 'replies', label: t('dashboard.metrics.replies'), icon: <Reply className="h-6 w-6 text-primary-foreground" />, format: count },
      { key: 'contactsReached', label: t('dashboard.metrics.contacts_reached'), icon: <Users className="h-6 w-6 text-primary-foreground" />, format: count },
      { key: 'interestedLeads', label: t('dashboard.metrics.interested_leads'), icon: <UserCheck className="h-6 w-6 text-primary-foreground" />, format: count },
      { key: 'mediaCreated', label: t('dashboard.metrics.media_created'), icon: <Image className="h-6 w-6 text-primary-foreground" />, format: count },
      { key: 'creditsPerReply', label: t('dashboard.metrics.credits_per_reply'), icon: <Coins className="h-6 w-6 text-primary-foreground" />, format: (v) => (v == null ? '—' : formatCredits(v)) },
      { key: 'optOuts', label: t('dashboard.metrics.opt_outs'), icon: <UserMinus className="h-6 w-6 text-primary-foreground" />, format: count },
    ];
    return tiles.map(({ key, label, icon, format }) => {
      const trend = insights.kpis[key];
      return {
        key,
        label,
        icon,
        value: format(trend?.value ?? null),
        change: trend?.change ?? null,
        changeUnit: trend?.unit,
        betterWhen: trend?.betterWhen,
      };
    });
  }, [insights, t]);

  const funnelData = useMemo(
    () => (insights?.funnel ?? []).map((s) => ({ stage: t(`dashboard.funnel.${s.stage}`, s.stage), count: s.count })),
    [insights, t],
  );

  const filteredCampaigns = useMemo(() => {
    if (statusFilter === 'all') return campaigns;
    return campaigns.filter((campaign) => campaign.status === statusFilter);
  }, [statusFilter, campaigns]);

  // The export is a CSV file, not JSON — it needs the bearer token, so it is fetched as a blob
  // rather than linked, exactly as the recipients export in CampaignResultsPanel is.
  const handleExport = () => {
    authFetch(`/workspaces/${workspaceId}/campaigns/export?status=${encodeURIComponent(statusFilter)}`)
      .then((response) => {
        if (!response.ok) throw new Error('export failed');
        // A workspace with nothing to export answers with the JSON envelope instead of a file.
        if ((response.headers.get('content-type') || '').includes('application/json')) return null;
        return response.blob();
      })
      .then((blob) => {
        if (!blob) {
          toast.info('No campaigns to export');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'campaigns.csv';
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Campaigns exported successfully');
      })
      .catch(() => toast.error('Could not export campaigns'));
  };

  const handleStatusChange = async (campaignId: string, newStatus: 'paused' | 'ended') => {
    try {
      await apiFetch(`/workspaces/${workspaceId}/campaigns/${campaignId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(newStatus === 'paused' ? 'Campaign paused' : 'Campaign ended');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update campaign status');
    } finally {
      // Stopping a send refunds what it never sent, so the balance moves too.
      refreshWorkspace();
    }
  };

  const handleDuplicate = async (campaignId: string) => {
    try {
      await apiFetch(`/workspaces/${workspaceId}/campaigns/${campaignId}/duplicate`, { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: dashboardKey });
      toast.success('Campaign duplicated as draft');
    } catch {
      toast.error('Failed to duplicate campaign');
    }
  };

  // Refetch the single campaign rather than reusing the list row: media URLs are
  // signed and expire (~24h), and only `GET /campaigns/:id` re-signs them. Without
  // this, a campaign older than a day previews with broken images and video.
  const viewCampaignDetails = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setDetailsOpen(true);
    try {
      const data = await apiFetch<{ campaign: Campaign }>(
        `/workspaces/${workspaceId}/campaigns/${campaign.id}`,
      );
      setSelectedCampaign((current) => (current?.id === campaign.id ? { ...data.campaign, stats: campaign.stats } : current));
    } catch {
      /* the list row is still shown; only its media may be stale */
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'live':
      case 'running':
        return 'bg-green-500/20 text-green-400';
      case 'paused':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'draft':
        return 'bg-gray-500/20 text-gray-400';
      case 'scheduled':
      case 'under_approval':
        return 'bg-blue-500/20 text-blue-400';
      case 'ended':
      case 'completed':
        return 'bg-purple-500/20 text-purple-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const activityLabel = (row: CreditActivity) =>
    t(`dashboard.credits.activity.${row.kind}_${row.category}`, { defaultValue: t('dashboard.credits.activity.other') });

  const goToBilling = () => navigate(`/w/${workspaceId}/billing`);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  const plan = overview?.credits.plan;
  const topUp = overview?.credits.topUp;
  const consumed = overview?.credits.consumed;
  const recent = overview?.credits.recent ?? [];
  const planName = overview?.workspace.planName || overview?.workspace.planKey;

  const planCaption = plan
    ? plan.usedPercent == null
      ? t('dashboard.credits.plan_no_allowance')
      : [
        t('dashboard.credits.plan_used', { percent: plan.usedPercent }),
        plan.renewsAt ? t('dashboard.credits.plan_resets', { date: formatDay(plan.renewsAt) }) : null,
        plan.carriedOver > 0 ? t('dashboard.credits.plan_carried_over', { amount: formatCredits(plan.carriedOver) }) : null,
      ].filter(Boolean).join(' · ')
    : null;

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <main className="flex-1 p-4 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-50 blur-3xl -z-10" />
        <div className="container mx-auto max-w-7xl space-y-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <BrandLogo size="sm" />
                <span className="text-xs uppercase tracking-[0.4em] text-muted-foreground hidden sm:block">
                  Targetna AI
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 text-foreground">
                {t('dashboard.title')}
              </h1>
              <p className="text-muted-foreground">
                Monitor your campaign performance and key metrics
              </p>
              {overviewQuery.dataUpdatedAt > 0 && (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  {overviewQuery.isFetching
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden />}
                  {t('dashboard.live', {
                    time: new Date(overviewQuery.dataUpdatedAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
                  })}
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" className="gap-2" onClick={handleExport}>
                <Download className="h-4 w-4" />
                {t('dashboard.actions.export')}
              </Button>
              {can('billing:manage') && (
                <Button
                  className="gap-2 bg-accent text-accent-foreground hover:bg-accent/80"
                  onClick={goToBilling}
                >
                  <ArrowUpRight className="h-4 w-4" />
                  {t('dashboard.actions.top_up')}
                </Button>
              )}
            </div>
          </div>

          {overviewQuery.isError && !overview && (
            <Card className="flex items-center justify-between gap-4 p-6">
              <p className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" /> Failed to load the dashboard.
              </p>
              <Button variant="outline" size="sm" onClick={() => overviewQuery.refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" /> Retry
              </Button>
            </Card>
          )}

          {overview && plan && topUp && consumed && (
            <>
              {/* The wallet: what is left of the plan, what is left of the top-ups, what was spent. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <CreditMeterCard
                  icon={<Database className="h-4 w-4" />}
                  title={planName
                    ? t('dashboard.credits.plan_title', { plan: planName })
                    : t('dashboard.credits.plan_title_fallback')}
                  value={`${formatCredits(plan.remaining)} / ${formatCredits(plan.allowance)}`}
                  percent={plan.usedPercent}
                  caption={planCaption}
                />

                <CreditMeterCard
                  icon={<Zap className="h-4 w-4" />}
                  title={t('dashboard.credits.top_up_title')}
                  value={topUp.purchased > 0
                    ? `${formatCredits(topUp.remaining)} / ${formatCredits(topUp.purchased)}`
                    : formatCredits(topUp.remaining)}
                  percent={topUp.usedPercent}
                  caption={topUp.purchased > 0
                    ? t('dashboard.credits.top_up_used', { used: formatCredits(topUp.used) })
                    : t('dashboard.credits.top_up_none')}
                >
                  {can('billing:manage') && (
                    <button type="button" onClick={goToBilling} className="text-xs font-medium text-primary underline">
                      {t('dashboard.actions.top_up')}
                    </button>
                  )}
                </CreditMeterCard>

                <CreditMeterCard
                  icon={<Flame className="h-4 w-4" />}
                  title={t('dashboard.credits.consumed_title')}
                  value={formatCredits(consumed.total)}
                  caption={consumed.since
                    ? t('dashboard.credits.consumed_since', { date: formatDay(consumed.since) })
                    : t('dashboard.credits.consumed_all_time')}
                >
                  <dl className="space-y-1 text-sm">
                    {SPENDING_CATEGORIES.map((category) => (
                      <div key={category} className="flex items-center justify-between">
                        <dt className="text-muted-foreground">{t(`dashboard.credits.category_${category}`)}</dt>
                        <dd className="font-medium tabular-nums">{formatCredits(consumed.byCategory[category])}</dd>
                      </div>
                    ))}
                  </dl>
                </CreditMeterCard>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <BarChart3 className="h-4 w-4" />
                    <span className="text-sm font-medium">{t('dashboard.overview.campaigns')}</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{overview.campaigns.total.toLocaleString()}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(overview.campaigns.byStatus).map(([status, count]) => (
                      <span
                        key={status}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(status)}`}
                      >
                        {status}: {count}
                      </span>
                    ))}
                  </div>
                </Card>

                <Card className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span className="text-sm font-medium">{t('dashboard.overview.contacts')}</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{overview.contacts.total.toLocaleString()}</p>
                  {overview.contacts.unsubscribed > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t('dashboard.overview.contacts_unsubscribed', { count: overview.contacts.unsubscribed })}
                    </p>
                  )}
                </Card>

                <Card className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <UserCheck className="h-4 w-4" />
                    <span className="text-sm font-medium">{t('dashboard.overview.team')}</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{overview.team.members.toLocaleString()}</p>
                </Card>
              </div>
            </>
          )}

          {/* KPI Grid — the last N days, each against the N days before. */}
          {insights && (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {t('dashboard.metrics.window', { days: insights.windowDays })}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {kpiTiles.map(({ key, ...tile }) => (
                  <KPIBox
                    key={key}
                    {...tile}
                    changeLabel={t('dashboard.metrics.vs_previous', { days: insights.windowDays })}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Messaging Analytics Section */}
          {messagingStats && messagingStats.total > 0 && (
            <Card className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold">Messaging Analytics</h2>
                </div>
                <Button variant="outline" size="sm" onClick={handlePollDelivery} disabled={pollingDelivery}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${pollingDelivery ? 'animate-spin' : ''}`} />
                  Refresh Delivery Status
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-lg border p-4 space-y-1">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Send className="h-4 w-4" />
                    <span className="text-sm">Total Sent</span>
                  </div>
                  <p className="text-2xl font-bold">{(messagingStats.sent + messagingStats.delivered + messagingStats.read).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border p-4 space-y-1">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Delivered</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{(messagingStats.delivered + messagingStats.read).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border p-4 space-y-1">
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm">Failed</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{messagingStats.failed.toLocaleString()}</p>
                </div>
                <div className="rounded-lg border p-4 space-y-1">
                  <div className="flex items-center gap-2 text-primary">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm">Delivery Rate</span>
                  </div>
                  <p className="text-2xl font-bold">{messagingStats.deliveryRate}%</p>
                </div>
              </div>

              {/* Channel Breakdown */}
              {Object.keys(messagingStats.byChannel).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(messagingStats.byChannel).map(([channel, data]) => (
                    <div key={channel} className="rounded-lg bg-muted/30 p-3 space-y-1">
                      <div className="flex items-center gap-2">
                        {channel === 'whatsapp' ? <MessageSquare className="h-4 w-4 text-green-500" /> : <Send className="h-4 w-4 text-blue-500" />}
                        <span className="text-sm font-medium capitalize">{channel}</span>
                      </div>
                      <p className="text-lg font-bold">{data.total.toLocaleString()}</p>
                      <div className="flex gap-3 text-xs text-muted-foreground">
                        <span className="text-green-600">{data.delivered + data.read} delivered</span>
                        <span className="text-red-600">{data.failed} failed</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Delivery Trend Chart */}
              {messagingStats.byDay.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-4">Daily Delivery Trend (Last 30 Days)</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={messagingStats.byDay} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        // `date` is the viewer's own calendar day ("2026-10-21"); parsed at local
                        // midnight so the label is that day, not UTC's.
                        tickFormatter={(d) => new Date(`${d}T00:00:00`).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                        className="text-xs"
                      />
                      <YAxis className="text-xs" />
                      <Tooltip
                        labelFormatter={(d) => new Date(`${d}T00:00:00`).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })}
                        contentStyle={{ borderRadius: '8px', border: '1px solid hsl(var(--border))' }}
                      />
                      <Legend />
                      <Bar dataKey="sent" name="Sent" fill="hsl(217, 91%, 60%)" radius={[2, 2, 0, 0]} stackId="a" />
                      <Bar dataKey="delivered" name="Delivered" fill="hsl(142, 71%, 45%)" radius={[2, 2, 0, 0]} stackId="a" />
                      <Bar dataKey="read" name="Read" fill="hsl(162, 73%, 35%)" radius={[2, 2, 0, 0]} stackId="a" />
                      <Bar dataKey="failed" name="Failed" fill="hsl(0, 84%, 60%)" radius={[2, 2, 0, 0]} stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Template Performance */}
              {messagingStats.templatePerformance.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Template Performance</h3>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Template</TableHead>
                          <TableHead className="text-right">Sent</TableHead>
                          <TableHead className="text-right">Delivered</TableHead>
                          <TableHead className="text-right">Failed</TableHead>
                          <TableHead className="text-right">Delivery Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {messagingStats.templatePerformance.map((tpl) => (
                          <TableRow key={tpl.name}>
                            <TableCell className="text-sm">{formatTemplateName(tpl.name)}</TableCell>
                            <TableCell className="text-right">{tpl.sent + tpl.delivered + tpl.read}</TableCell>
                            <TableCell className="text-right text-green-600">{tpl.delivered + tpl.read}</TableCell>
                            <TableCell className="text-right text-red-600">{tpl.failed}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={tpl.deliveryRate >= 80 ? 'default' : tpl.deliveryRate >= 50 ? 'secondary' : 'destructive'}>
                                {tpl.deliveryRate}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </Card>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6 lg:col-span-2 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">{t('dashboard.filters.status')}</h2>
                  <p className="text-sm text-muted-foreground">
                    {t('dashboard.filters.visual')}
                  </p>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder={t('dashboard.filters.all')} />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="all">{t('dashboard.filters.all')}</SelectItem>
                    {STATUS_FILTERS.map((status) => (
                      <SelectItem key={status} value={status}>{t(`dashboard.filters.${status}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="overflow-x-auto -mx-4 md:mx-0">
                {filteredCampaigns.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No campaigns found</p>
                    <Button
                      variant="link"
                      className="mt-2"
                      onClick={() => navigate('/w/' + workspaceId + '/campaigns/media')}
                    >
                      Create your first media
                    </Button>
                  </div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground text-left">
                        <th className="py-2 px-4">{t('dashboard.table.campaign')}</th>
                        <th className="py-2 px-4">{t('dashboard.table.status')}</th>
                        <th className="py-2 px-4">{t('dashboard.table.type')}</th>
                        <th className="py-2 px-4 text-right">{t('dashboard.table.messages')}</th>
                        <th className="py-2 px-4 text-right">{t('dashboard.table.delivered')}</th>
                        <th className="py-2 px-4 text-right">{t('dashboard.table.replies')}</th>
                        <th className="py-2 px-4 text-right">{t('dashboard.table.credits')}</th>
                        <th className="py-2 px-4 text-right">{t('dashboard.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCampaigns.map((campaign) => (
                        <tr key={campaign.id} className="border-t border-border/60 hover:bg-muted/30">
                          <td className="py-3 px-4 font-medium">{campaign.name}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 capitalize">{campaign.type}</td>
                          <td className="py-3 px-4 text-right tabular-nums">{(campaign.stats?.sent ?? 0).toLocaleString()}</td>
                          <td className="py-3 px-4 text-right tabular-nums">
                            {(campaign.stats?.delivered ?? 0).toLocaleString()}
                            {campaign.stats?.deliveryRate != null && (
                              <span className="text-xs text-muted-foreground"> ({campaign.stats.deliveryRate}%)</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right tabular-nums">{(campaign.stats?.replies ?? 0).toLocaleString()}</td>
                          <td className="py-3 px-4 text-right tabular-nums">{formatCredits(campaign.stats?.credits ?? 0)}</td>
                          <td className="py-3 px-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => viewCampaignDetails(campaign)}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/w/${workspaceId}/campaigns/${campaign.id}`)}>
                                  {isLaunched(campaign.status)
                                    ? <><Eye className="h-4 w-4 mr-2" />Results</>
                                    : <><RefreshCw className="h-4 w-4 mr-2" />Open draft</>}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicate(campaign.id)}>
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                {campaign.status !== 'live' && campaign.status !== 'running' && (
                                  <DropdownMenuItem onClick={() => navigate(`/w/${workspaceId}/campaigns/${campaign.id}`)}>
                                    <Play className="h-4 w-4 mr-2" />
                                    Set up and launch
                                  </DropdownMenuItem>
                                )}
                                {/* Only while it is actually sending: pausing stops the send. */}
                                {campaign.status === 'running' && (
                                  <DropdownMenuItem onClick={() => setConfirmAction({ campaignId: campaign.id, campaignName: campaign.name, action: 'paused' })}>
                                    <Pause className="h-4 w-4 mr-2" />
                                    Pause Campaign
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => setConfirmAction({ campaignId: campaign.id, campaignName: campaign.name, action: 'ended' })}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  End Campaign
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>

            {/* Every change to the balance, newest first — the ledger behind the cards above. */}
            <Card className="p-6 space-y-4 bg-[hsl(var(--surface-overlay))]">
              <h2 className="text-xl font-semibold text-foreground">{t('dashboard.credits.activity_title')}</h2>
              {recent.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('dashboard.credits.activity_empty')}</p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {recent.map((row) => {
                    const opening = row.kind === 'opening_balance';
                    return (
                      <li key={row.id} className="flex items-start justify-between gap-3 py-2.5 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{activityLabel(row)}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {row.reference?.name ? `${row.reference.name} · ` : ''}
                            {new Date(row.createdAt).toLocaleString(i18n.language, {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            })}
                          </p>
                        </div>
                        <div className="shrink-0 text-end">
                          <p className={`font-semibold tabular-nums ${opening ? '' : row.amount < 0 ? 'text-destructive' : 'text-success'}`}>
                            {!opening && row.amount > 0 ? '+' : ''}{formatCredits(row.amount)}
                          </p>
                          {row.balanceAfter != null && !opening && (
                            <p className="text-xs text-muted-foreground tabular-nums">
                              {t('dashboard.credits.activity_balance', { balance: formatCredits(row.balanceAfter) })}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {can('billing:manage') && (
                <Button variant="outline" className="w-full" onClick={goToBilling}>
                  {t('dashboard.actions.top_up')}
                </Button>
              )}
            </Card>
          </div>

          {/* Funnel Chart */}
          {insights && (
            <PyramidChart title={t('dashboard.funnel_title')} data={funnelData} emptyText={t('dashboard.funnel.empty')} />
          )}
        </div>
      </main>

      {/* Campaign Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCampaign?.name}</DialogTitle>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t('dashboard.table.status')}</Label>
                  <p className="font-medium capitalize">{selectedCampaign.status}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('dashboard.table.type')}</Label>
                  <p className="font-medium capitalize">{selectedCampaign.type}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('dashboard.table.messages')}</Label>
                  <p className="font-medium">{(selectedCampaign.stats?.sent ?? 0).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('dashboard.table.delivered')}</Label>
                  <p className="font-medium">
                    {(selectedCampaign.stats?.delivered ?? 0).toLocaleString()}
                    {selectedCampaign.stats?.deliveryRate != null && ` (${selectedCampaign.stats.deliveryRate}%)`}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('dashboard.table.replies')}</Label>
                  <p className="font-medium">{(selectedCampaign.stats?.replies ?? 0).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('dashboard.table.credits')}</Label>
                  <p className="font-medium">{formatCredits(selectedCampaign.stats?.credits ?? 0)}</p>
                </div>
              </div>

              {/* The campaign's own page answers "what is this" far better than a dialog can - it
                  shows the message exactly as WhatsApp renders it, plus the per-recipient results -
                  so this links there instead of trying to reproduce a worse version here. */}
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => {
                    setDetailsOpen(false);
                    navigate(`/w/${workspaceId}/campaigns/${selectedCampaign.id}`);
                  }}
                  className="flex-1"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View campaign
                </Button>
                {selectedCampaign.status === 'running' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDetailsOpen(false);
                      setConfirmAction({ campaignId: selectedCampaign.id, campaignName: selectedCampaign.name, action: 'paused' });
                    }}
                    className="flex-1"
                  >
                    <Pause className="h-4 w-4 mr-2" />
                    Pause Campaign
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Confirmation Dialog for Destructive Actions */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.action === 'ended' ? t('dashboard.confirm.end_title') : t('dashboard.confirm.pause_title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.action === 'ended'
                ? t('dashboard.confirm.end_body', { name: confirmAction?.campaignName })
                : t('dashboard.confirm.pause_body', { name: confirmAction?.campaignName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction?.action === 'ended' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              onClick={() => {
                if (confirmAction) {
                  handleStatusChange(confirmAction.campaignId, confirmAction.action);
                  setConfirmAction(null);
                }
              }}
            >
              {confirmAction?.action === 'ended' ? t('dashboard.confirm.end_title') : t('dashboard.confirm.pause_title')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
